import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@monksflow/monks-ui';
import { ExternalLinkIcon } from 'lucide-react';

export interface UrlFieldValue {
  url: string | null;
}

export interface UrlFieldConfig {
  allowedSchemes?: string[];
}

interface UrlFieldProps {
  value: UrlFieldValue | null;
  config: UrlFieldConfig;
  disabled?: boolean;
  onChange: (next: UrlFieldValue) => void;
}

/** Parses `url` and returns its scheme (without the trailing `:`), or null if malformed. */
function schemeOf(url: string): string | null {
  try {
    return new URL(url).protocol.replace(':', '');
  } catch {
    return null;
  }
}

/**
 * Pure, controlled. A click-through link is offered only when the entered URL parses AND its
 * scheme is in `config.allowedSchemes` (default `['https']`) — the spec's one enforced
 * safety rule for this type.
 */
export function UrlField({ value, config, disabled = false, onChange }: UrlFieldProps) {
  const { t } = useTranslation('projects');
  const url = value?.url ?? '';
  // eslint-disable-next-line i18next/no-literal-string -- protocol default, not copy
  const allowedSchemes = config.allowedSchemes ?? ['https'];
  const scheme = url ? schemeOf(url) : null;
  const isClickable = scheme != null && allowedSchemes.includes(scheme);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Input
          type="text"
          value={url}
          disabled={disabled}
          placeholder={t('projectBriefDetail.briefSectionField.urlField.placeholder')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ url: e.target.value || null })}
          className="flex-1"
        />
        {isClickable && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={t('projectBriefDetail.briefSectionField.urlField.openLink')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ExternalLinkIcon className="size-4" />
          </a>
        )}
      </div>
    </div>
  );
}
